import { AnchoredTooltip } from "@repo/ui/tooltip/anchoredTooltip";
import { type FC, useId, useLayoutEffect, useRef, useState } from "react";

export type MovementCategory = "improving" | "flat" | "declining";

export const MOVEMENT_COLORS: Record<MovementCategory, string> = {
  improving: "#10b981", // emerald-500
  flat: "#9ca3af", // gray-400
  declining: "#f87171", // red-400
};

export interface ScoreMovementPoint {
  readonly id: string;
  readonly startScore: number;
  readonly currentScore: number;
  readonly category: MovementCategory;
  readonly tooltipContent: string;
  readonly onClick?: () => void;
}

interface AxisRange {
  readonly axisMin: number;
  readonly axisMax: number;
  readonly axisTicks: readonly number[];
}

/**
 * Compute axis range and tick marks for a score movement scatter chart.
 * For yes_no: 0-1 percentage range, 5% floor/ceiling, 10% tick intervals.
 * For 1_5_score: 1-5 range, 0.5 floor/ceiling, 0.5 or 1.0 tick intervals.
 */
export function computeScoreAxisRange(
  scores: readonly {
    readonly startScore: number;
    readonly currentScore: number;
  }[],
  gradingValueType: "yes_no" | "1_5_score",
): AxisRange {
  if (gradingValueType === "1_5_score") {
    if (scores.length === 0) {
      return { axisMin: 1, axisMax: 5, axisTicks: [1, 2, 3, 4, 5] };
    }

    let minScore = 5;
    let maxScore = 1;
    for (const t of scores) {
      minScore = Math.min(minScore, t.startScore, t.currentScore);
      maxScore = Math.max(maxScore, t.startScore, t.currentScore);
    }

    const axisMin = Math.max(1, Math.floor(minScore * 2) / 2);
    const axisMax = Math.min(5, Math.ceil(maxScore * 2) / 2);

    const ticks: number[] = [];
    const range = axisMax - axisMin;
    const step = range <= 2 ? 0.5 : 1;
    for (let t = axisMin; t <= axisMax + 0.001; t += step) {
      ticks.push(Math.round(t * 10) / 10);
    }

    return { axisMin, axisMax, axisTicks: ticks };
  } else {
    if (scores.length === 0) {
      return {
        axisMin: 0,
        axisMax: 1,
        axisTicks: [0, 0.1, 0.2, 0.3, 0.4, 0.5, 0.6, 0.7, 0.8, 0.9, 1],
      };
    }

    let minScore = 1;
    let maxScore = 0;
    for (const t of scores) {
      minScore = Math.min(minScore, t.startScore, t.currentScore);
      maxScore = Math.max(maxScore, t.startScore, t.currentScore);
    }

    const axisMin = Math.max(0, Math.floor(minScore * 20) / 20);
    const axisMax = Math.min(1, Math.ceil(maxScore * 20) / 20);

    const ticks: number[] = [];
    const tickStart = Math.floor(axisMin * 10) / 10;
    for (let t = tickStart; t <= axisMax + 0.001; t += 0.1) {
      const rounded = Math.round(t * 100) / 100;
      if (rounded >= axisMin && rounded <= axisMax) {
        ticks.push(rounded);
      }
    }

    return { axisMin, axisMax, axisTicks: ticks };
  }
}

export function formatScore(
  score: number,
  gradingValueType: "yes_no" | "1_5_score",
): string {
  if (gradingValueType === "yes_no") {
    return `${Math.round(score * 100)}%`;
  }
  return score.toFixed(1);
}

export function formatScoreDelta(
  delta: number,
  gradingValueType: "yes_no" | "1_5_score",
): string {
  const sign = delta >= 0 ? "+" : "";
  if (gradingValueType === "yes_no") {
    return `${sign}${Math.round(delta * 100)}pts`;
  }
  return `${sign}${delta.toFixed(1)}`;
}

export function formatScoreAxisLabel(
  tick: number,
  gradingValueType: "yes_no" | "1_5_score",
): string {
  if (gradingValueType === "yes_no") {
    return `${Math.round(tick * 100)}%`;
  }
  return tick.toFixed(1);
}

interface ScoreMovementScatterChartProps {
  readonly points: readonly ScoreMovementPoint[];
  readonly axisMin: number;
  readonly axisMax: number;
  readonly axisTicks: readonly number[];
  readonly formatAxisLabel: (tick: number) => string;
  readonly xAxisTitle: string;
  readonly yAxisTitle: string;
}

export const ScoreMovementScatterChart: FC<ScoreMovementScatterChartProps> = ({
  points,
  axisMin,
  axisMax,
  axisTicks,
  formatAxisLabel,
  xAxisTitle,
  yAxisTitle,
}) => {
  const [width, setWidth] = useState(800);
  const [height, setHeight] = useState(400);
  const svgContainerRef = useRef<HTMLDivElement>(null);
  const markerId = useId();
  // SVG shapes can't be React Aria tooltip triggers, so the chart tracks the
  // hovered point itself and anchors one tooltip to it.
  const hoveredPointRef = useRef<SVGCircleElement | null>(null);
  const [hoveredTooltipContent, setHoveredTooltipContent] = useState<
    string | null
  >(null);

  const padding = { top: 20, right: 20, bottom: 48, left: 58 };
  const chartWidth = width - padding.left - padding.right;
  const chartHeight = height - padding.top - padding.bottom;

  useLayoutEffect(() => {
    const svgContainer = svgContainerRef.current;
    if (!svgContainer) return;

    const window = svgContainer.ownerDocument.defaultView;
    if (!window) return;

    const updateSize = () => {
      setWidth(svgContainer.clientWidth);
      setHeight(svgContainer.clientHeight);
    };

    updateSize();

    const observer = new window.ResizeObserver(() => {
      updateSize();
    });
    observer.observe(svgContainer);

    return () => {
      observer.disconnect();
    };
  }, []);

  const range = axisMax - axisMin;
  const safeRange = range === 0 ? 0.1 : range;

  const toX = (score: number) =>
    padding.left + ((score - axisMin) / safeRange) * chartWidth;
  const toY = (score: number) =>
    padding.top + ((axisMax - score) / safeRange) * chartHeight;

  const improvingArrowId = `${markerId}-arrow-improving`;
  const decliningArrowId = `${markerId}-arrow-declining`;

  return (
    <div ref={svgContainerRef} className="relative h-80 w-full">
      <svg
        className="absolute size-full"
        viewBox={`0 0 ${width} ${height}`}
        preserveAspectRatio="xMidYMid meet"
      >
        <defs>
          <marker
            id={improvingArrowId}
            markerWidth="4"
            markerHeight="4"
            refX="2"
            refY="2"
            orient="auto"
          >
            <path d="M0,0 L4,2 L0,4 L1,2 Z" fill={MOVEMENT_COLORS.improving} />
          </marker>
          <marker
            id={decliningArrowId}
            markerWidth="4"
            markerHeight="4"
            refX="2"
            refY="2"
            orient="auto"
          >
            <path d="M0,0 L4,2 L0,4 L1,2 Z" fill={MOVEMENT_COLORS.declining} />
          </marker>
        </defs>

        {/* Grid lines */}
        {axisTicks.map((tick) => (
          <g key={`grid-${tick}`}>
            <line
              x1={toX(tick)}
              y1={padding.top}
              x2={toX(tick)}
              y2={height - padding.bottom}
              stroke="#e5e7eb"
              strokeWidth="1"
            />
            <line
              x1={padding.left}
              y1={toY(tick)}
              x2={width - padding.right}
              y2={toY(tick)}
              stroke="#e5e7eb"
              strokeWidth="1"
            />
          </g>
        ))}

        {/* Diagonal "flat" line */}
        <line
          x1={toX(axisMin)}
          y1={toY(axisMin)}
          x2={toX(axisMax)}
          y2={toY(axisMax)}
          stroke="#64748b"
          strokeWidth="1.5"
          strokeDasharray="6 4"
        />

        {/* X-axis labels */}
        {axisTicks.map((tick) => (
          <text
            key={`x-label-${tick}`}
            x={toX(tick)}
            y={height - padding.bottom + 20}
            textAnchor="middle"
            className="fill-gray-500 text-[11px]"
          >
            {formatAxisLabel(tick)}
          </text>
        ))}

        {/* Y-axis labels */}
        {axisTicks.map((tick) => (
          <text
            key={`y-label-${tick}`}
            x={padding.left - 10}
            y={toY(tick)}
            textAnchor="end"
            dominantBaseline="middle"
            className="fill-gray-500 text-[11px]"
          >
            {formatAxisLabel(tick)}
          </text>
        ))}

        {/* Axis titles */}
        <text
          x={padding.left + chartWidth / 2}
          y={height - 5}
          textAnchor="middle"
          className="fill-gray-600 text-xs font-medium"
        >
          {xAxisTitle}
        </text>
        <text
          x={12}
          y={padding.top + chartHeight / 2}
          textAnchor="middle"
          transform={`rotate(-90, 12, ${padding.top + chartHeight / 2})`}
          className="fill-gray-600 text-xs font-medium"
        >
          {yAxisTitle}
        </text>

        {/* Movement arrows for each point */}
        {points.map((point) => {
          const color = MOVEMENT_COLORS[point.category];
          const startX = toX(point.startScore);
          const startY = toY(point.startScore);
          const endX = toX(point.startScore);
          const endY = toY(point.currentScore);
          const dy = endY - startY;

          if (point.category === "flat") {
            return (
              <circle
                key={point.id}
                cx={endX}
                cy={endY}
                r={4}
                fill={color}
                stroke="white"
                strokeWidth="1"
                onMouseEnter={(event) => {
                  hoveredPointRef.current = event.currentTarget;
                  setHoveredTooltipContent(point.tooltipContent);
                }}
                onMouseLeave={() => setHoveredTooltipContent(null)}
                className={point.onClick ? "cursor-pointer" : "cursor-default"}
                onClick={point.onClick}
              />
            );
          }

          const arrowMarkerId =
            point.category === "improving"
              ? improvingArrowId
              : decliningArrowId;
          const circleRadius = 4;
          const lineStartY = startY + (dy > 0 ? circleRadius : -circleRadius);
          const lineEndY =
            endY + (dy > 0 ? -circleRadius - 2 : circleRadius + 2);

          return (
            <g key={point.id}>
              {/* Starting point (on diagonal) - smaller, subtle */}
              <circle
                cx={startX}
                cy={startY}
                r={3}
                fill={color}
                fillOpacity={0.3}
                stroke={color}
                strokeWidth="1"
              />
              {/* Arrow line */}
              <line
                x1={startX}
                y1={lineStartY}
                x2={endX}
                y2={lineEndY}
                stroke={color}
                strokeWidth="1.5"
                strokeLinecap="round"
                markerEnd={`url(#${arrowMarkerId})`}
              />
              {/* End point - solid and prominent */}
              <circle
                cx={endX}
                cy={endY}
                r={4}
                fill={color}
                stroke="white"
                strokeWidth="1"
                onMouseEnter={(event) => {
                  hoveredPointRef.current = event.currentTarget;
                  setHoveredTooltipContent(point.tooltipContent);
                }}
                onMouseLeave={() => setHoveredTooltipContent(null)}
                className={point.onClick ? "cursor-pointer" : "cursor-default"}
                onClick={point.onClick}
              />
            </g>
          );
        })}
      </svg>

      {hoveredTooltipContent !== null ? (
        <AnchoredTooltip triggerRef={hoveredPointRef}>
          {/* The content is built with newlines rather than markup. */}
          <span className="whitespace-pre-line">{hoveredTooltipContent}</span>
        </AnchoredTooltip>
      ) : null}
    </div>
  );
};
